import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Animated, Easing, Platform, View, ViewStyle } from "react-native"
import { Lottie, Screen, Text, updateSystemNavigatorBar } from "../../components"
import { useStores } from "../../models"
import { color } from "../../theme"
import { initialSyncAllV2 } from "../../database"
import numeral from "numeral"
import { AppStack, LoginStackSplash, resetRoot } from "../../navigators"
import { delay } from "../../utils/delay"
import { useKeepAwake } from "@sayem314/react-native-keep-awake";

const lottieInitialSync = require('./initial-sync.json');

const ROOT: ViewStyle = {
  flexGrow: 1
}

const LOTTIE: ViewStyle = {
  flexGrow: 1
}

const LOADING: ViewStyle = {
  height: 186,
  justifyContent: "center"
}

export const InitialSyncScreen: FC = observer(function InitialSyncScreen(props) {
  // Pull in one of our MST stores
  const {
    initialSyncStore, initialSyncStore: { setSynced, progress, setProgress },
    appStore, userStore, tokenStore
  } = useStores()
  const lottieProgress = useRef<Animated.Value>(new Animated.Value(0)).current
  const lottieWave = useRef<Animated.Value>(new Animated.Value(0)).current
  const [ lottieProgressValue, setLottieProgressValue ] = useState<number>(0)
  const [ lottieWaveValue, setLottieWaveValue ] = useState<number>(0)
  const [ finished, setFinished ] = useState<boolean>(false)

  useKeepAwake()

  useEffect(() => {
    initialSyncStore.init()
  }, [initialSyncStore])

  const initialSync = useCallback(() => {
    lottieProgress.stopAnimation();
    Animated.timing(lottieProgress, {
      toValue: (28 / 72),
      duration: 2000,
      easing: Easing.linear,
      useNativeDriver: Platform.OS !== 'web'
    }).start(() => {
      lottieWave.stopAnimation();
      lottieWave.setValue(0)
      Animated.spring(lottieWave, {
        toValue: 2 / 72,
        damping: 0.001,
        mass: 20,
        useNativeDriver: Platform.OS !== 'web'
      }).start()
      initialSyncAllV2(setProgress)
        .then((success) => {
          if (success){
            appStore.set("syncedFarm", true)
            setFinished(true)
          } else {
            lottieProgress.stopAnimation();
            Animated.timing(lottieProgress,  {
              toValue: (28 / 72),
              duration: 1000,
              easing: Easing.linear,
              useNativeDriver: Platform.OS !== 'web'
            }).start(() => {
              appStore.removeDatabase().finally(() => {
                setFinished(false)
                setProgress(0)
                initialSync()
              })
            })
          }
        })
    })
  }, [appStore, lottieProgress, lottieWave, setProgress])

  useEffect(() => {
    userStore.getUser(false)
      .then(() => {
        if (!userStore.user) resetRoot(LoginStackSplash);
        else initialSync()
      }).catch(() => {
        resetRoot(LoginStackSplash);
      })
  }, [initialSync, tokenStore, userStore])

  useEffect(() => {
    if (Platform.OS === 'web') {
      lottieProgress.addListener(({ value }) => setLottieProgressValue(value))
      lottieWave.addListener(({ value }) => setLottieWaveValue(value))
    }

    return () => {
      if (Platform.OS === 'web') {
        lottieProgress.removeAllListeners()
        lottieWave.removeAllListeners()
      }
    }
  }, [initialSync, initialSyncStore, lottieProgress, lottieWave])

  useEffect(() => {
    if (finished) {
      lottieProgress.stopAnimation();
      Animated.timing(lottieProgress,  {
        toValue: 50 / 72,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== 'web'
      }).start(() => {
        lottieProgress.stopAnimation()
        lottieProgress.removeAllListeners()
        lottieWave.stopAnimation()
        lottieWave.removeAllListeners()
        delay(1).then(async () => {
          setSynced(true)
          await updateSystemNavigatorBar({
            colorScheme: "light",
            hideNavigationBar: false,
            navigationBarBackgroundColor: color.palette.white,
            backgroundColor: color.primary,
            statusBarStyle: "light-content",
            statusBarBackgroundColor: color.primary
          })
          resetRoot(AppStack)
        });
      })
    }
  }, [finished, lottieProgress, lottieWave, setSynced])

  useEffect(() => {
    if (finished || progress === 0) return;
    lottieProgress.stopAnimation();
    Animated.timing(lottieProgress, {
      toValue: (progress / 100) * (20 / 72) + (28 / 72),
      duration: 1000,
      easing: Easing.linear,
      useNativeDriver: Platform.OS !== 'web'
    }).start();
  }, [finished, lottieProgress, progress])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      backgroundColor={color.primary}
      backgroundBar={color.primary}
      statusBar="light-content"
      hideNavigationBar={true}
    >
      <View style={LOTTIE}>
        <Lottie
          source={lottieInitialSync}
          progress={Platform.OS === 'web' ? (lottieProgressValue - lottieWaveValue) : Animated.subtract(lottieProgress, lottieWave)}
        />
      </View>
      <View style={LOADING}>
        <Text textAlign="center">
          <Text tx="initialSyncScreen.loading" preset="title3" color={color.palette.white} />
          <Text text={` ${numeral(progress).format('0,0')}%`} preset="title3" color={color.palette.white} />
        </Text>
      </View>
    </Screen>
  )
})
